import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addNcentralConfig,
  getIntegrationMappedCustomers,
  getIntegrationMappedDevices,
  getNcentralConfig,
  getNcentralCustomers,
  getNcentralDevices,
  getNcentralRemoteCredentials,
  mapNcentralCustomers,
  mapNcentralDevices,
  updateNcentralConfig,
  updateNcentralRemoteCredentials,
} from "controller/api";
import { ApiErrorType } from "shared";
import {
  AddNCentralConfigInput,
  NcentralDeviceMappingInput,
  UpdateNcentralRemoteCredentialsInput,
} from "shared/types/integrations";
import { sentryLogger } from "utilz";
import { debugConsole } from "utilz/helpers";

export const useGetNcentralCustomers = () => {
  return useQuery({
    queryKey: ["ncentralCustomers"],
    queryFn: () => getNcentralCustomers(),
    onError: (err) => debugConsole(err),
    // staleTime: Infinity,
  });
};

export const useGetNcentralDevices = () => {
  return useQuery({
    queryKey: ["ncentralDevices"],
    queryFn: () => getNcentralDevices(),
    onError: (err) => debugConsole(err),
    // staleTime: Infinity,
  });
};

export const useAddNCentralConfig = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: AddNCentralConfigInput) => addNcentralConfig(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ncentralConfig"]);
      },
      onError(error: ApiErrorType) {
        debugConsole(error);
        sentryLogger({
          error: error,
          workflow: "integration",
          level: "error",
          functionName: "addNcentralConfig",
        });
      },
    }
  );
};

export const useUpdateNCentralConfig = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: AddNCentralConfigInput) => updateNcentralConfig(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ncentralConfig"]);
      },
      onError(error: ApiErrorType) {
        debugConsole(error);
        sentryLogger({
          error: error,
          workflow: "integration",
          level: "error",
          functionName: "updateNcentralConfig",
        });
      },
    }
  );
};

export const useGetNcentralConfig = ({
  enabled = true,
}: {
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ["ncentralConfig"],
    queryFn: () => getNcentralConfig(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        functionName: "getNcentralConfig",
      });
    },
    enabled: enabled ? true : false,
    // staleTime: Infinity,
  });
};

export const useMapNcentralDevices = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: NcentralDeviceMappingInput) => mapNcentralDevices(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["integrationDevices", variables.source]);
      },
    }
  );
};
export const useGetIntegrationMappedDevices = (source: string) => {
  return useQuery({
    queryKey: ["integrationDevices", source],
    queryFn: () => getIntegrationMappedDevices(source),
    onError: (err) => debugConsole(err),
    // staleTime: Infinity,
  });
};

export const useUpdateNcentralRemoteCredentials = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: UpdateNcentralRemoteCredentialsInput) =>
      updateNcentralRemoteCredentials(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ncentralRemoteCredentials"]);
      },
    }
  );
};

export const useNcentralRemoteCredentials = () => {
  return useQuery({
    queryKey: ["ncentralRemoteCredentials"],
    queryFn: () => getNcentralRemoteCredentials(),
    onError: (err) => debugConsole(err),
    // staleTime: Infinity,
  });
};
